import { Layout } from "antd";
import { useTranslation } from "react-i18next";
import { Navigate, useParams } from "react-router";

import WidgetsGrid from "@/components/Elements/WidgetsGrid";
import BaseHeader from "@/components/Header/BaseHeader";
import HeaderButtons from "@/components/Header/Project";
import InterventionsTable from "@/components/ListTable/InterventionsTable";
import ProjectCostsCard from "@/components/Project/ProjectCostsCard";
import { STORAGE_KEYS } from "@/constants/StorageKeys";
import { TAB_KEYS } from "@/constants/TabKeys";
import {
  INTERVENTIONS_FILTERS,
  INTERVENTIONS_INCLUDES,
} from "@/entities/interventions";
import { PROJECT_INCLUDES, useGetProject } from "@/entities/project";
import Associate from "@/features/Projects/Associate";
import { ListProvider } from "@/modules/list";
import ListTableSummary from "@/modules/list-table-summary";
import ProjectBreadcrumb from "@/modules/project/project-breadcrumb";
import { useSidePanel } from "@/modules/side-panel";
import { TranslateCost } from "@/modules/translate";
import { isError404 } from "@/services/api";
import { ContextTabs } from "@/ui/context-tabs";

const { Content } = Layout;

const Details = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { openSidePanel } = useSidePanel();

  const include = [PROJECT_INCLUDES.USERS];

  const { isLoading, project, error, removeQuery } = useGetProject(id, {
    params: { include: include.join(",") },
  });

  const WIDGETS = [
    {
      key: "additional_costs_card",
      colSpan: 10,
      component: <ProjectCostsCard loading={isLoading} project={project} />,
    },
  ];

  const onAddIntervention = () => {
    openSidePanel({
      title: t("project:form.associateWith"),
      component: (
        <Associate
          project={project}
          model="interventions"
          label={t("global:interventionPlural")}
          placeholder={t("global:interventionPlural")}
        />
      ),
      size: "large",
    });
  };

  const tabs = [
    {
      key: TAB_KEYS.PROJECTS_INTERVENTIONS,
      label: t("global:interventionPlural"),
      children: (
        <ListProvider
          model="interventions"
          loading={isLoading}
          storageKey={STORAGE_KEYS.PROJECTS_INTERVENTIONS}
          contextTabValue={TAB_KEYS.PROJECTS_INTERVENTIONS}
          hiddenFilters={{ [INTERVENTIONS_FILTERS.PROJECT_ID]: id }}
          includes={[
            INTERVENTIONS_INCLUDES.USERS,
            INTERVENTIONS_INCLUDES.TEMPLATE,
          ]}
        >
          <InterventionsTable
            onAddButton={onAddIntervention}
            addButtonText={t("project:associate.button")}
            dissociate={{
              model: project,
              field: "project_id",
              invalidateKeys: [["projects", "get"]],
            }}
            summary={() => (
              <ListTableSummary
                label={t("project:list.interventionsTotalCost")}
                gutterColSpan={6}
                valueAlign="right"
                hasBatchControls={true}
                value={
                  <TranslateCost value={project?.interventions_cost ?? 0} />
                }
              />
            )}
          />
        </ListProvider>
      ),
    },
  ];

  if (isError404(error)) {
    removeQuery();
    return <Navigate to="/projects" />;
  }

  return (
    <>
      <BaseHeader
        title={project?.name}
        loading={isLoading}
        extra={
          !isLoading && [
            <HeaderButtons key="HeaderButtons" project={project} />,
          ]
        }
        breadcrumb={<ProjectBreadcrumb project={project} />}
      />
      <Content>
        <WidgetsGrid widgets={WIDGETS}>
          <ContextTabs defaultActiveKey="interventions" items={tabs} />
        </WidgetsGrid>
      </Content>
    </>
  );
};

export default Details;
